"use client";

import { c as _c } from "react/compiler-runtime";
import { useGesture } from "@use-gesture/react";
import Image from "next/image";
import * as React from "react";
interface PinchZoomImageProps {
  src: string;
  alt: string;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  fetchPriority?: "high" | "low";
  loading?: "eager" | "lazy";
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

/**
 * PinchZoomImage - Enables pinch-to-zoom for images (specifically floor plans)
 * Uses @use-gesture/react for lightweight, reliable pinch-to-zoom (only 8.9KB gzipped!)
 */
export function PinchZoomImage(t0) {
  const $ = _c(41);
  const {
    src,
    alt,
    sizes,
    quality,
    priority,
    fetchPriority,
    loading,
    onError
  } = t0;
  const containerRef = React.useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0,
      scale: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [style, setStyle] = React.useState(t1);
  let t2;
  if ($[1] !== style.scale || $[2] !== style.x || $[3] !== style.y) {
    t2 = t3 => {
      const {
        offset: t4,
        origin: t5,
        first,
        memo,
        event
      } = t3;
      const [scale] = t4;
      const [ox, oy] = t5;
      event.stopPropagation();
      const clampedScale = Math.max(1, Math.min(5, scale));
      if (first) {
        const container = containerRef.current;
        if (!container) {
          return;
        }
        const rect = container.getBoundingClientRect();
        const relativeX = ox - rect.left - rect.width / 2;
        const relativeY = oy - rect.top - rect.height / 2;
        return {
          initialX: style.x,
          initialY: style.y,
          initialScale: style.scale,
          originX: relativeX,
          originY: relativeY
        };
      }
      if (!memo) {
        return;
      }
      const scaleChange = clampedScale / memo.initialScale;
      const newX = memo.initialX - memo.originX * (scaleChange - 1);
      const newY = memo.initialY - memo.originY * (scaleChange - 1);
      setStyle({
        x: newX,
        y: newY,
        scale: clampedScale
      });
      return memo;
    };
    $[1] = style.scale;
    $[2] = style.x;
    $[3] = style.y;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== style.scale) {
    t3 = t4 => {
      const {
        offset: t5,
        pinching,
        tap,
        event: event_0
      } = t4;
      const [x, y] = t5;
      if (tap) {
        return;
      }
      if (pinching) {
        return;
      }
      if (style.scale > 1) {
        event_0.stopPropagation();
      } else {
        return;
      }
      const container_0 = containerRef.current;
      if (!container_0) {
        return;
      }
      const {
        width,
        height
      } = container_0.getBoundingClientRect();
      const maxX = width * (style.scale - 1) / 2;
      const maxY = height * (style.scale - 1) / 2;
      const clampedX = Math.min(maxX, Math.max(-maxX, x));
      const clampedY = Math.min(maxY, Math.max(-maxY, y));
      setStyle(prev => ({
        ...prev,
        x: clampedX,
        y: clampedY
      }));
    };
    $[5] = style.scale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = {
      onPinch: t2,
      onDrag: t3
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      passive: false
    };
    t6 = {
      scaleBounds: {
        min: 1,
        max: 5
      },
      rubberband: true
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== style.x || $[13] !== style.y) {
    t7 = {
      target: containerRef,
      eventOptions: t5,
      pinch: t6,
      drag: {
        from: () => [style.x, style.y],
        filterTaps: true
      }
    };
    $[12] = style.x;
    $[13] = style.y;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useGesture(t4, t7);
  const lastTapRef = React.useRef(0);
  let t8;
  if ($[15] !== style.scale) {
    t8 = e => {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapRef.current;
      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        e.preventDefault();
        const container_1 = containerRef.current;
        if (!container_1) {
          return;
        }
        const rect_0 = container_1.getBoundingClientRect();
        const [clientX, clientY] = "changedTouches" in e ? [e.changedTouches[0].clientX, e.changedTouches[0].clientY] : [e.clientX, e.clientY];
        const tapX = clientX - rect_0.left - rect_0.width / 2;
        const tapY = clientY - rect_0.top - rect_0.height / 2;
        if (style.scale > 1) {
          setStyle({
            x: 0,
            y: 0,
            scale: 1
          });
        } else {
          const newX_0 = -tapX * 1.5;
          const newY_0 = -tapY * 1.5;
          setStyle({
            x: newX_0,
            y: newY_0,
            scale: 2.5
          });
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    };
    $[15] = style.scale;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleDoubleTap = t8;
  const t9 = style.scale > 1 ? "grab" : "default";
  let t10;
  if ($[17] !== t9) {
    t10 = {
      cursor: t9
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = `translate(${style.x}px, ${style.y}px) scale(${style.scale})`;
  const t12 = style.scale === 1 || style.scale === 2.5 ? "transform 0.3s ease-out" : "none";
  let t13;
  if ($[19] !== t11 || $[20] !== t12) {
    t13 = {
      transform: t11,
      transformOrigin: "center center",
      width: "100%",
      height: "100%",
      position: "relative",
      transition: t12
    };
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = sizes || "100vw";
  let t15;
  if ($[22] !== src) {
    t15 = src.endsWith(".svg");
    $[22] = src;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== alt || $[25] !== fetchPriority || $[26] !== loading || $[27] !== onError || $[28] !== priority || $[29] !== quality || $[30] !== src || $[31] !== t14 || $[32] !== t15) {
    t16 = <Image src={src} alt={alt} fill={true} className="object-contain select-none pointer-events-none" draggable={false} sizes={t14} quality={quality} priority={priority} fetchPriority={fetchPriority} loading={loading} onError={onError} unoptimized={t15} />;
    $[24] = alt;
    $[25] = fetchPriority;
    $[26] = loading;
    $[27] = onError;
    $[28] = priority;
    $[29] = quality;
    $[30] = src;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t13 || $[35] !== t16) {
    t17 = <div style={t13}>{t16}</div>;
    $[34] = t13;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== handleDoubleTap || $[38] !== t10 || $[39] !== t17) {
    t18 = <div ref={containerRef} className="absolute inset-0 w-full h-full overflow-hidden touch-none" onClick={handleDoubleTap} style={t10}>{t17}</div>;
    $[37] = handleDoubleTap;
    $[38] = t10;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}